import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { builderSessionId, useMetadataBuilderStore } from "../../stores/metadata-builder-store";
import { useMetadataHistoryStore } from "../../stores/metadata-history-store";
import { useMetadataEditingStore } from "../../stores/metadata-editing-store";
import { weekdayKeys } from "../../lib/schedule-time/schedule-dates";
import { createOfflineLayout } from "../../lib/metadata-builder/offline-layout";
import { dispatchBuilderMetadata } from "../../lib/metadata-builder/edit-text";
import { endMetadataMerge } from "../../lib/metadata-history/dispatch";
import { Button } from "../ui/button";
import { OfflineMessageForm } from "../schedule/OfflineMessageForm";
import { metadataLabels } from "../editor-panel/developer-section/metadata-form-schema";
import { DayPreviewControls } from "./DayPreviewControls";
import { DayAnchorControls } from "./DayAnchorControls";
import { GlobalElementPlacementControls } from "./GlobalElementPlacementControls";
import { DayLayoutExceptionControls } from "./DayLayoutExceptionControls";
import { TextStyleControls } from "./TextStyleControls";

export function OfflineStep() {
  const { t } = useTranslation();
  const session = useMetadataHistoryStore(state => state.sessions[builderSessionId]);
  const day = useMetadataBuilderStore(state => state.selectedDay);
  const started = useMetadataBuilderStore(state => state.offlineStarted);
  const sample = useMetadataBuilderStore(state => state.sampleOfflineMessage);
  const sampleActive = useMetadataBuilderStore(state => state.sampleOfflineActive);
  const setSample = useMetadataBuilderStore(state => state.setSampleOffline);
  const selectedPath = useMetadataEditingStore(state => state.selected);
  const layout = session?.dayLayouts?.offline;
  const metadata = session?.projection;
  const artwork = session?.artwork;
  const dayIndex = weekdayKeys.indexOf(day);
  const source = artwork?.offline[dayIndex];

  useEffect(() => {
    if (!metadata || !artwork || layout || started) return;
    // Keep this view flag outside history so Undo can remove initialization.
    useMetadataBuilderStore.setState({ offlineStarted: true });
    dispatchBuilderMetadata(createOfflineLayout(metadata, artwork));
    useMetadataEditingStore.getState().select("days.mon.offline.0");
  }, [metadata, artwork, layout, started]);
  useEffect(() => {
    const selectedDay = weekdayKeys.find(key => selectedPath?.startsWith(`days.${key}.`));
    if (selectedDay) useMetadataBuilderStore.setState({ selectedDay });
  }, [selectedPath]);

  if (!metadata || !artwork) return null;
  if (!layout) return <Button variant="outline" onClick={() => {
    endMetadataMerge();
    dispatchBuilderMetadata(createOfflineLayout(metadata, artwork));
  }}>{t("builder.offline.add")}</Button>;
  const element = (layout.exceptions[day] ?? layout.shared)[0];
  const styleName = element?.style;
  const suffix = `0${dayIndex + 1}`;
  const fallback = artwork.missingOffline.includes(`c${suffix}`);
  return (
    <div className="space-y-3">
      <DayPreviewControls />
      {fallback && <p className="text-neutral-500">{t("builder.offline.fallback", { missing: `c${suffix}`, source: `a${suffix}` })}</p>}
      <div className="space-y-1" onFocusCapture={endMetadataMerge}>
        <OfflineMessageForm message={sample ?? t("builder.sampleOffline")} active={sampleActive}
          day={metadataLabels(t)[day]!} onChange={setSample} onBlur={endMetadataMerge} />
        <p className="text-neutral-500">{t("builder.offline.sampleHelp")}</p>
      </div>
      <DayLayoutExceptionControls metadata={metadata} layout={layout} day={day} />
      {source && <DayAnchorControls layout={layout} day={day} source={source} />}
      <h4 className="text-xs font-medium">{t("builder.offline.textPlacement")}</h4>
      <GlobalElementPlacementControls layout={layout} day={day} index={0} />
      {styleName && <TextStyleControls key={styleName} styleName={styleName} />}
    </div>
  );
}
